import { useEffect, useCallback } from 'react';
import type { AnnotationTool } from '../types';
import type { AnnotationAction } from '../annotationReducer';

interface UseAnnotationKeyboardOptions {
  dispatch: React.Dispatch<AnnotationAction>;
  selectedIds: Set<string>;
  nudge: (dx: number, dy: number) => void;
  containerRef: React.RefObject<HTMLElement | null>;
}

const NUDGE_AMOUNT = 1;
const SHIFT_NUDGE_AMOUNT = 10;

const TOOL_HOTKEYS: Record<string, AnnotationTool> = {
  b: 'box',
  o: 'circle',
  l: 'line',
  a: 'arrow',
  f: 'freeform',
  t: 'text',
  e: 'eraser',
};

export function useAnnotationKeyboard({
  dispatch,
  selectedIds,
  nudge,
  containerRef,
}: UseAnnotationKeyboardOptions) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Ignore if typing in an input or contentEditable element.
      // Use composedPath()[0] to see through Shadow DOM boundaries (e.g. <comment-textarea>).
      const target = e.target as HTMLElement;
      const actualTarget = (e.composedPath()[0] ?? target) as HTMLElement;
      if (
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' ||
        target.isContentEditable || actualTarget.isContentEditable
      ) {
        return;
      }

      const key = e.key.toLowerCase();

      // Delete/Backspace
      if (key === 'delete' || key === 'backspace') {
        if (selectedIds.size > 0) {
          e.preventDefault();
          e.stopPropagation();
          dispatch({ type: 'DELETE_SELECTED' });
          return;
        }
      }

      // Undo: Ctrl+Z / Cmd+Z
      if (key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: 'UNDO' });
        return;
      }

      // Redo: Ctrl+Y / Cmd+Shift+Z
      if (
        (key === 'y' && (e.ctrlKey || e.metaKey)) ||
        (key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey)
      ) {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: 'REDO' });
        return;
      }

      // Arrow keys — nudge selected shapes. stopImmediatePropagation prevents
      // the image viewer's pan handler (also on document) from also firing.
      if (key === 'arrowup' || key === 'arrowdown' || key === 'arrowleft' || key === 'arrowright') {
        if (selectedIds.size > 0) {
          e.preventDefault();
          e.stopImmediatePropagation();
          const amount = e.shiftKey ? SHIFT_NUDGE_AMOUNT : NUDGE_AMOUNT;
          switch (key) {
            case 'arrowup':
              nudge(0, -amount);
              break;
            case 'arrowdown':
              nudge(0, amount);
              break;
            case 'arrowleft':
              nudge(-amount, 0);
              break;
            case 'arrowright':
              nudge(amount, 0);
              break;
          }
          return;
        }
      }

      // Escape — always deselect
      if (key === 'escape') {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: 'DESELECT_ALL' });
        return;
      }

      // Tool hotkeys (only when no modifier keys)
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const tool = TOOL_HOTKEYS[key];
        if (tool) {
          e.preventDefault();
          e.stopPropagation();
          dispatch({ type: 'SET_TOOL', tool });
          return;
        }
      }
    },
    [dispatch, selectedIds, nudge],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}
